<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CrmPropertiesAPI from 'dashboard/api/crmProperties';

const store = useStore();

const isLoading = ref(false);
const properties = ref([]);

const currentChat = computed(() => store.getters.getSelectedChat || {});
const currentUser = computed(() => store.getters.getCurrentUser || {});
const currentCustomAttributes = computed(
  () => currentChat.value.custom_attributes || {}
);
const selectedProperty = computed(
  () => currentCustomAttributes.value.selected_property || null
);
const selectedPropertyId = computed(
  () => selectedProperty.value?.id?.toString() || ''
);
const placeholderLabel = computed(() =>
  isLoading.value ? 'Cargando propiedades...' : 'Sin propiedad'
);

const normalizeProperty = property => {
  const id =
    property.id ??
    property.ID ??
    property.Id ??
    property.codigo ??
    property.Codigo;
  const description =
    property.descripcion ??
    property.Descripcion ??
    property.description ??
    property.Description ??
    property.nombre ??
    property.Nombre ??
    '';
  const price =
    property.precio ??
    property.Precio ??
    property.price ??
    property.Price ??
    '';

  return {
    ...property,
    id,
    description,
    price,
  };
};

const normalizeResponse = response => {
  const body = response?.data || {};
  const list = Array.isArray(body)
    ? body
    : body.payload || body.data || body.propiedades || body.properties || [];

  return Array.isArray(list)
    ? list.map(normalizeProperty).filter(item => item.id)
    : [];
};

const formatPrice = price => {
  if (price === null || price === undefined || price === '') return '';
  return `${price}`;
};

const optionLabel = property => {
  const price = formatPrice(property.price);
  return [property.id, property.description, price].filter(Boolean).join(' - ');
};

const fetchProperties = async () => {
  if (!currentUser.value.email) return;

  isLoading.value = true;
  try {
    const response = await CrmPropertiesAPI.get();
    properties.value = normalizeResponse(response);
  } catch (error) {
    properties.value = [];
  } finally {
    isLoading.value = false;
  }
};

const updateSelectedProperty = async event => {
  const propertyId = event.target.value;
  const nextAttributes = { ...currentCustomAttributes.value };

  if (!propertyId) {
    delete nextAttributes.selected_property;
  } else {
    const property = properties.value.find(
      item => item.id?.toString() === propertyId
    );
    nextAttributes.selected_property = property;
  }

  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: currentChat.value.id,
      customAttributes: nextAttributes,
    });
  } catch (error) {
    useAlert('No se pudo actualizar la propiedad del chat');
  }
};

onMounted(fetchProperties);

watch(
  () => currentUser.value.email,
  () => fetchProperties()
);
</script>

<template>
  <select
    class="property-selector hidden h-8 w-32 max-w-[32vw] rounded-lg border border-n-container bg-n-button-color px-2 text-sm text-n-slate-12 shadow outline-none hover:bg-n-alpha-2 focus:border-n-brand disabled:cursor-not-allowed disabled:opacity-60 sm:block xl:w-40"
    :value="selectedPropertyId"
    :disabled="isLoading || !currentChat.id"
    @change="updateSelectedProperty"
  >
    <option value="">
      {{ placeholderLabel }}
    </option>
    <option
      v-for="property in properties"
      :key="property.id"
      :value="property.id"
    >
      {{ optionLabel(property) }}
    </option>
  </select>
</template>

<style scoped>
.property-selector {
  line-height: 1.25rem;
}
</style>
